// Высота нижней панели текущего экрана — чтобы плашка уведомления вставала
// НАД ней, а не поверх.
// Source: docs/specs/design-revision/a-notifications.md §2.1.
//
// Почему не константа: панели разные. У `BottomNav` одна высота, у sticky-CTA
// на карточке показателя своя, на экранах авторизации панели нет вовсе.
// Плюс safe-area меняется при повороте устройства.
//
// Почему не одна переменная «последний записавший выиграл»: при переходе между
// экранами размонтирование старой панели могло затереть значение, которое уже
// поставила новая (GAP-9). Поэтому владельцы считаются поимённо, а в CSS уходит
// максимум — так плашка гарантированно чистит самую высокую панель на экране.

import { useLayoutEffect, useRef } from 'react';

const CSS_VAR = '--hl-bottom-bar';

/** owner → его высота в пикселях. Ключ — стабильный объект-метка владельца. */
const owners = new Map<object, number>();

function flush(): void {
  const root = document.documentElement;
  if (owners.size === 0) {
    root.style.removeProperty(CSS_VAR);
    return;
  }
  const tallest = Math.max(...owners.values());
  root.style.setProperty(CSS_VAR, `${tallest}px`);
}

/** Только для тестов: сбрасывает реестр владельцев между прогонами. */
export function __resetBottomBarInset(): void {
  owners.clear();
  document.documentElement.style.removeProperty(CSS_VAR);
}

/**
 * Регистрирует элемент как нижнюю панель экрана.
 * Подключается в `BottomNav` и в sticky-CTA карточки показателя.
 */
export function useBottomBarInset<T extends HTMLElement>(ref: React.RefObject<T | null>): void {
  const token = useRef<object>({});

  useLayoutEffect(() => {
    const el = ref.current;
    const me = token.current;
    if (!el) return undefined;

    const measure = () => {
      owners.set(me, el.offsetHeight);
      flush();
    };

    measure();

    // Поворот устройства и появление клавиатуры меняют safe-area, а с ней
    // и высоту панели.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);

    return () => {
      observer?.disconnect();
      owners.delete(me);
      flush();
    };
  }, [ref]);
}
